/**
 * Google One Tap sign-up for the public site.
 *
 * The prompt appears top-right for any visitor signed into Google who is not already
 * signed in here. Picking an account posts the ID token Google issues to
 * `/api/auth/google/`, which creates the account (or matches an existing one) and unlocks
 * the first-quote welcome discount.
 *
 * The visitor stays on the page (ADR 0078). On success the website sets the `ss_signed_in`
 * marker cookie itself (`AuthState.markSignedIn`), swaps the navbar to "Go to portal"
 * (`AuthState.apply`) and shows a short "You're signed in" card, cloned from the
 * `#google-one-tap-success` template, with an optional "Go to customer portal" link. The
 * JWTs that come back are discarded: never stored, never put in a URL. The portal lives on
 * another origin and cannot read this one's storage anyway; the marker is all it needs.
 *
 * The prompt is not offered when the marker is present, and `auto_select` is off. The
 * portal's sign-out only clears the marker, so with auto-select the website would sign
 * someone straight back in after they signed out. Here a sign-in always follows an
 * explicit account pick. A rejected or failed sign-in stays silent: no marker, no card.
 *
 * Config comes from `#google-one-tap`'s data attributes so this file needs no inline
 * script. Loaded from base.html after Google's GSI client and after `auth-state.js` (all
 * deferred, so they run in document order); `window.AuthState` is read lazily at use.
 */
class GoogleOneTap {
    constructor(root) {
        this.root = root;
        this.clientId = root.dataset.clientId || '';
        this.endpoint = root.dataset.endpoint || '/api/auth/google/';
        this.portalUrl = root.dataset.portalUrl || '';
        this.cookieDomain = root.dataset.cookieDomain || '';
    }

    init() {
        if (!this.clientId) return;
        if (!window.google || !window.google.accounts || !window.google.accounts.id) {
            // GSI failed to load (blocked by an extension, offline, etc.). Nothing to do —
            // the site works fine without it.
            return;
        }
        // Already signed in (the marker cookie): no banner.
        if (window.AuthState && window.AuthState.isSignedIn()) return;

        window.google.accounts.id.initialize({
            client_id: this.clientId,
            callback: (response) => this.handleCredential(response),
            // Never sign anyone in silently. The portal's sign-out only clears the marker
            // cookie, so auto-select would sign a visitor straight back in on this site
            // right after they signed out. A sign-in always follows an explicit pick.
            auto_select: false,
            // An accidental click elsewhere on the page shouldn't kill the prompt.
            cancel_on_tap_outside: false,
            context: 'signup',
        });
        window.google.accounts.id.prompt();
    }

    async handleCredential(response) {
        if (!response || !response.credential) return;

        try {
            const result = await fetch(this.endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    credential: response.credential,
                    timezone: this.timezone(),
                }),
            });

            if (!result.ok) {
                // A disabled or locked account lands here. The visitor did not ask to sign
                // in on a marketing page, so nothing is shown and no marker is set — they
                // can still use the portal's login page, which explains what went wrong.
                console.debug('[GoogleOneTap] Sign-in rejected:', result.status);
                return;
            }

            const payload = await result.json();
            const user = payload && payload.data && payload.data.user;
            const email = user && user.email;

            // Stay on the page: record the sign-in for the navbar and tell the visitor.
            // This comes before analytics so nothing there can get in its way. An older
            // cached auth-state bundle may lack markSignedIn; the card still shows then.
            const auth = window.AuthState;
            if (auth && typeof auth.markSignedIn === 'function') {
                try {
                    auth.markSignedIn(this.cookieDomain);
                    if (typeof auth.apply === 'function') auth.apply();
                } catch (error) {
                    console.debug('[GoogleOneTap] Could not record the sign-in:', error);
                }
            }
            this.showSuccess(user && typeof user.first_name === 'string' ? user.first_name.trim() : '');

            // Server-side already captured the sign-up itself; this joins the visitor's
            // existing anonymous session to that person on the same lower-cased email.
            try {
                if (email && typeof window.identifyUser === 'function') {
                    window.identifyUser(email);
                }
                if (typeof window.trackEvent === 'function') {
                    window.trackEvent('google_one_tap_signed_in');
                }
            } catch (error) {
                console.debug('[GoogleOneTap] Analytics failed:', error);
            }
        } catch (error) {
            console.debug('[GoogleOneTap] Sign-in failed:', error);
        }
    }

    /** Show the "You're signed in" card. Never throws, so it cannot break the sign-in. */
    showSuccess(firstName) {
        try {
            const template = document.getElementById('google-one-tap-success');
            if (!template || !template.content) return;
            // A second sign-in replaces the card: run the old one's full cleanup (timer,
            // keydown listener) and drop it at once.
            if (this.dismissCard) this.dismissCard(true);
            document.querySelectorAll('.gotap-card').forEach((old) => old.remove());

            const card = template.content.firstElementChild.cloneNode(true);
            const title = card.querySelector('[data-gotap-title]');
            // textContent only: the name is user-controlled.
            if (title && firstName) title.textContent = `You're signed in, ${firstName}`;

            const link = card.querySelector('[data-gotap-portal]');
            if (link) {
                if (this.portalUrl) link.setAttribute('href', this.portalUrl);
                else link.remove();
            }
            // Without a portal link the sentence must not mention the portal either.
            const sentence = card.querySelector('[data-gotap-text]');
            if (sentence && !this.portalUrl) sentence.textContent = 'You can keep browsing.';

            // Sit directly below the visible navbar pill so the card never hides the quote
            // button, account menu or hamburger. Falls back to the CSS default (88px).
            const bar = document.querySelector('#main-navbar .nav-bar') || document.getElementById('main-navbar');
            if (bar && typeof bar.getBoundingClientRect === 'function') {
                const bottom = bar.getBoundingClientRect().bottom;
                if (Number.isFinite(bottom) && bottom > 0) card.style.setProperty('--gotap-top', `${Math.round(bottom + 12)}px`);
            }

            let timer = null;
            let paused = false;
            let closed = false;
            let hovering = false;
            const onKey = (event) => {
                if (event.key === 'Escape') dismiss();
            };
            // The card sits above the Account menu panel and the mobile drawer, so opening
            // either one closes it. Listeners only observe: the menus still open as usual.
            const onMenuOpen = () => dismiss();
            const menuBindings = [];
            const bindMenu = (id, types) => {
                const el = document.getElementById(id);
                if (!el) return;
                types.forEach((type) => {
                    el.addEventListener(type, onMenuOpen);
                    menuBindings.push([el, type]);
                });
            };
            const unbindMenus = () => {
                menuBindings.splice(0).forEach(([el, type]) => el.removeEventListener(type, onMenuOpen));
            };
            const dismiss = (immediate = false) => {
                if (closed) return;
                closed = true;
                clearTimeout(timer);
                this.clearAnnouncement();
                document.removeEventListener('keydown', onKey);
                unbindMenus();
                if (this.dismissCard === dismiss) this.dismissCard = null;
                if (immediate === true) {
                    card.remove();
                    return;
                }
                card.classList.add('gotap-leaving');
                setTimeout(() => card.remove(), 260);
            };
            const arm = () => {
                if (closed) return;
                clearTimeout(timer);
                timer = setTimeout(() => {
                    if (paused) return;
                    dismiss();
                }, GoogleOneTap.AUTO_DISMISS_MS);
            };
            const pause = () => {
                paused = true;
                clearTimeout(timer);
            };
            const resume = () => {
                if (hovering || card.contains(document.activeElement)) return;
                paused = false;
                arm();
            };

            const keep = card.querySelector('[data-gotap-dismiss]');
            if (keep) keep.addEventListener('click', () => dismiss());
            // The link opens a new tab; close the card so it does not sit paused by focus
            // when the visitor comes back.
            if (link && card.contains(link)) {
                link.addEventListener('click', () => dismiss());
            }
            // Only a real mouse pauses: a stationary cursor where the card appears, or a
            // touch tap on the card body, must not leave it stuck on screen.
            card.addEventListener('pointermove', (event) => {
                if (event.pointerType !== 'mouse') return;
                hovering = true;
                pause();
            });
            card.addEventListener('pointerleave', (event) => {
                if (event.pointerType !== 'mouse') return;
                hovering = false;
                resume();
            });
            card.addEventListener('focusin', pause);
            card.addEventListener('focusout', () => setTimeout(resume, 0));
            document.addEventListener('keydown', onKey);
            bindMenu('account-dropdown-container', ['mouseenter', 'focusin', 'click']);
            bindMenu('mobile-menu-btn', ['click']);

            this.dismissCard = dismiss;
            document.body.appendChild(card);
            arm();
            this.announce(title ? title.textContent : "You're signed in");
        } catch (error) {
            console.debug('[GoogleOneTap] Could not show the success card:', error);
        }
    }

    /**
     * Speak the sentence through the page's persistent, empty `role="status"` region. A
     * live region announces changes, not text that arrives already inside a new node, so
     * the region exists from page load and is only filled here.
     */
    announce(text) {
        try {
            const region = document.getElementById('google-one-tap-live');
            if (!region) return;
            region.textContent = '';
            clearTimeout(this.announceTimer);
            this.announceTimer = setTimeout(() => {
                region.textContent = text;
            }, 50);
        } catch (error) {
            // Announcing is a nicety; never let it break the card.
        }
    }

    /** Empty the live region and cancel a pending announcement (the card is gone). */
    clearAnnouncement() {
        try {
            clearTimeout(this.announceTimer);
            const region = document.getElementById('google-one-tap-live');
            if (region) region.textContent = '';
        } catch (error) {
            // Nothing to clean up.
        }
    }

    timezone() {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        } catch (error) {
            return '';
        }
    }
}

GoogleOneTap.AUTO_DISMISS_MS = 10000;

(function initGoogleOneTap() {
    const root = document.getElementById('google-one-tap');
    if (root) new GoogleOneTap(root).init();
})();
